import React, { useState, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import styles from './BulkImportModal.module.css';

export interface ColumnDef {
  key: string;
  label: string;
  required?: boolean;
  type?: 'string' | 'number' | 'boolean';
}

export interface BulkImportModalProps {
  resourceName: string;
  columns: ColumnDef[];
  onImport: (records: Record<string, unknown>[]) => Promise<unknown>;
  onClose: () => void;
}

type Step = 'pick' | 'mapping' | 'preview' | 'importing' | 'done' | 'error';

interface RowValidation {
  row: Record<string, unknown>;
  errors: string[];
}

// Normalize header strings: lowercase, replace spaces/dashes with underscores, trim
function normalizeHeader(h: string): string {
  return h.trim().toLowerCase().replace(/[\s-]+/g, '_');
}

// Common aliases
const ALIASES: Record<string, string> = {
  product_name: 'name',
  item_name: 'name',
  waste_factor: 'factor',
  material: 'material_type',
  type: 'material_type',
  description: 'description',
  content: 'description',
  vendor_sku_number: 'vendor_sku',
  supplier: 'vendor',
  supplier_sku: 'vendor_sku',
  lead_time: 'lead_time_days',
  lead_days: 'lead_time_days',
  stock: 'in_stock',
  in_stock_flag: 'in_stock',
  subcategory: 'subcategory',
  sub_category: 'subcategory',
  title_name: 'title',
};

function autoMapHeaders(
  csvHeaders: string[],
  columns: ColumnDef[],
): Record<string, string> {
  const mapping: Record<string, string> = {};
  const colKeys = columns.map((c) => c.key);

  for (const header of csvHeaders) {
    const norm = normalizeHeader(header);

    // Exact match
    if (colKeys.includes(norm)) {
      mapping[header] = norm;
      continue;
    }

    // Alias match
    if (ALIASES[norm] && colKeys.includes(ALIASES[norm])) {
      mapping[header] = ALIASES[norm];
      continue;
    }

    // Partial match: check if the normalized header contains a column key
    const partial = colKeys.find(
      (k) => norm.includes(k) || k.includes(norm),
    );
    if (partial) {
      mapping[header] = partial;
      continue;
    }

    // No match
    mapping[header] = '';
  }

  return mapping;
}

function parseValue(
  raw: unknown,
  type: ColumnDef['type'],
): unknown {
  if (raw === undefined || raw === null || raw === '') return type === 'number' ? 0 : type === 'boolean' ? false : '';

  if (type === 'number') {
    const n = Number(raw);
    return isNaN(n) ? 0 : n;
  }

  if (type === 'boolean') {
    const s = String(raw).toLowerCase().trim();
    return s === '1' || s === 'true' || s === 'yes' || s === 'y';
  }

  return String(raw).trim();
}

function validateRow(
  row: Record<string, unknown>,
  columns: ColumnDef[],
): string[] {
  const errors: string[] = [];
  for (const col of columns) {
    if (col.required) {
      const val = row[col.key];
      if (val === undefined || val === null || val === '') {
        errors.push(`${col.label} is required`);
      }
    }
  }
  return errors;
}

export const PRACTICE_COLUMNS: ColumnDef[] = [
  { key: 'title', label: 'Title', required: true },
  { key: 'description', label: 'Description', required: true },
  { key: 'category', label: 'Category' },
  { key: 'subcategory', label: 'Subcategory' },
  { key: 'priority', label: 'Priority' },
];

export const PRODUCT_COLUMNS: ColumnDef[] = [
  { key: 'sku', label: 'SKU' },
  { key: 'name', label: 'Name', required: true },
  { key: 'brand', label: 'Brand' },
  { key: 'category', label: 'Category' },
  { key: 'subcategory', label: 'Subcategory' },
  { key: 'price', label: 'Price', type: 'number' },
  { key: 'vendor', label: 'Vendor' },
  { key: 'vendor_sku', label: 'Vendor SKU' },
  { key: 'lead_time_days', label: 'Lead Time Days', type: 'number' },
  { key: 'in_stock', label: 'In Stock', type: 'boolean' },
  { key: 'unit', label: 'Unit' },
  { key: 'notes', label: 'Notes' },
];

export const WASTE_FACTOR_COLUMNS: ColumnDef[] = [
  { key: 'material_type', label: 'Material Type', required: true },
  { key: 'pattern', label: 'Pattern' },
  { key: 'factor', label: 'Factor', type: 'number', required: true },
  { key: 'notes', label: 'Notes' },
];

const PREVIEW_LIMIT = 10;

export const BulkImportModal: React.FC<BulkImportModalProps> = ({
  resourceName,
  columns,
  onImport,
  onClose,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<Step>('pick');
  const [rawRows, setRawRows] = useState<Record<string, unknown>[]>([]);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [validated, setValidated] = useState<RowValidation[]>([]);
  const [importedCount, setImportedCount] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const [dragActive, setDragActive] = useState(false);

  const processFile = useCallback(
    (file: File) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' });

          if (rows.length === 0) {
            setErrorMsg('File contains no data rows.');
            setStep('error');
            return;
          }

          const headers = Object.keys(rows[0]);
          setCsvHeaders(headers);
          setRawRows(rows);

          const autoMap = autoMapHeaders(headers, columns);
          setMapping(autoMap);

          // Check if any required columns are unmapped
          const requiredKeys = columns.filter((c) => c.required).map((c) => c.key);
          const mappedKeys = new Set(Object.values(autoMap).filter(Boolean));
          const missingRequired = requiredKeys.filter((k) => !mappedKeys.has(k));

          if (missingRequired.length > 0) {
            setStep('mapping');
          } else {
            // Auto-advance to preview
            applyMapping(rows, headers, autoMap);
          }
        } catch {
          setErrorMsg('Failed to parse file. Ensure it is a valid CSV or XLSX.');
          setStep('error');
        }
      };
      reader.readAsArrayBuffer(file);
    },
    [columns],
  );

  const applyMapping = useCallback(
    (rows: Record<string, unknown>[], headers: string[], map: Record<string, string>) => {
      const colMap = new Map(columns.map((c) => [c.key, c]));

      const mapped: RowValidation[] = rows.map((raw) => {
        const row: Record<string, unknown> = {};
        for (const h of headers) {
          const targetKey = map[h];
          if (!targetKey) continue;
          const colDef = colMap.get(targetKey);
          row[targetKey] = parseValue(raw[h], colDef?.type);
        }
        const errors = validateRow(row, columns);
        return { row, errors };
      });

      setValidated(mapped);
      setStep('preview');
    },
    [columns],
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) processFile(file);
    },
    [processFile],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragActive(false);
      const file = e.dataTransfer.files?.[0];
      if (file) processFile(file);
    },
    [processFile],
  );

  const handleMappingChange = useCallback(
    (csvHeader: string, targetKey: string) => {
      setMapping((prev) => ({ ...prev, [csvHeader]: targetKey }));
    },
    [],
  );

  const confirmMapping = useCallback(() => {
    applyMapping(rawRows, csvHeaders, mapping);
  }, [rawRows, csvHeaders, mapping, applyMapping]);

  const validRows = validated.filter((v) => v.errors.length === 0);
  const errorRows = validated.filter((v) => v.errors.length > 0);

  const doImport = useCallback(
    async (rows: RowValidation[]) => {
      setStep('importing');
      try {
        const records = rows.map((v) => v.row);
        const result = await onImport(records) as { inserted?: number };
        setImportedCount(result?.inserted ?? records.length);
        setStep('done');
      } catch (err) {
        setErrorMsg(err instanceof Error ? err.message : 'Import failed');
        setStep('error');
      }
    },
    [onImport],
  );

  // Mapped column keys that have at least one mapping
  const mappedColumnKeys = new Set(Object.values(mapping).filter(Boolean));

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h3 className={styles.modalTitle}>
          Import {resourceName}
          <div className={styles.subtitle}>Upload a CSV or XLSX file</div>
        </h3>

        {/* ── Step: File picker ──────────────────────────────────── */}
        {step === 'pick' && (
          <>
            <div
              className={`${styles.dropZone} ${dragActive ? styles.dropZoneActive : ''}`}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={handleDrop}
            >
              <div className={styles.dropIcon}>&#128196;</div>
              <div className={styles.dropText}>Click or drag file here</div>
              <div className={styles.dropHint}>Supports .csv, .xlsx, .xls</div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <div className={styles.actions}>
              <button className={styles.btnCancel} onClick={onClose}>Cancel</button>
            </div>
          </>
        )}

        {/* ── Step: Column mapping ──────────────────────────────── */}
        {step === 'mapping' && (
          <>
            <div className={styles.mappingSection}>
              <div className={styles.mappingLabel}>
                Map CSV columns to {resourceName.toLowerCase()} fields
              </div>
              <div className={styles.mappingGrid}>
                {csvHeaders.map((h) => (
                  <React.Fragment key={h}>
                    <div className={styles.mappingField}>{h}</div>
                    <div className={styles.mappingArrow}>&rarr;</div>
                    <div>
                      <select
                        className={styles.mappingSelect}
                        value={mapping[h] || ''}
                        onChange={(e) => handleMappingChange(h, e.target.value)}
                      >
                        <option value="">(skip)</option>
                        {columns.map((col) => (
                          <option key={col.key} value={col.key}>
                            {col.label}{col.required ? ' *' : ''}
                          </option>
                        ))}
                      </select>
                      {columns.find((c) => c.key === mapping[h])?.required && !mapping[h] && (
                        <span className={styles.mappingRequired}> Required</span>
                      )}
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
            <div className={styles.actions}>
              <button className={styles.btnCancel} onClick={() => setStep('pick')}>Back</button>
              <button
                className={styles.btnImport}
                onClick={confirmMapping}
                disabled={columns.filter((c) => c.required).some((c) => !mappedColumnKeys.has(c.key))}
              >
                Preview
              </button>
            </div>
          </>
        )}

        {/* ── Step: Preview ─────────────────────────────────────── */}
        {step === 'preview' && (
          <>
            <div className={styles.previewWrap}>
              <table className={styles.previewTable}>
                <thead>
                  <tr>
                    <th>#</th>
                    {columns.filter((c) => mappedColumnKeys.has(c.key) || c.required).map((col) => (
                      <th key={col.key}>{col.label}{col.required ? ' *' : ''}</th>
                    ))}
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {validated.slice(0, PREVIEW_LIMIT).map((v, i) => {
                    const hasErr = v.errors.length > 0;
                    return (
                      <tr key={i} className={hasErr ? styles.errorRow : undefined}>
                        <td>{i + 1}</td>
                        {columns.filter((c) => mappedColumnKeys.has(c.key) || c.required).map((col) => {
                          const val = v.row[col.key];
                          const isMissing = col.required && (val === undefined || val === null || val === '');
                          return (
                            <td key={col.key} className={isMissing ? styles.errorCell : undefined}>
                              {val !== undefined && val !== null ? String(val) : ''}
                            </td>
                          );
                        })}
                        <td className={hasErr ? styles.errorCell : undefined}>
                          {hasErr ? v.errors.join(', ') : 'OK'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {validated.length > PREVIEW_LIMIT && (
                <div className={styles.moreRows}>
                  ... and {validated.length - PREVIEW_LIMIT} more rows
                </div>
              )}
            </div>

            {/* Summary */}
            {errorRows.length === 0 && (
              <div className={`${styles.summary} ${styles.summaryOk}`}>
                <span className={styles.summaryCount}>{validRows.length}</span> rows ready to import
              </div>
            )}
            {errorRows.length > 0 && validRows.length > 0 && (
              <div className={`${styles.summary} ${styles.summaryWarn}`}>
                <span className={styles.summaryCount}>{validRows.length}</span> valid,{' '}
                <span className={styles.summaryCount}>{errorRows.length}</span> with errors
              </div>
            )}
            {validRows.length === 0 && errorRows.length > 0 && (
              <div className={`${styles.summary} ${styles.summaryError}`}>
                All <span className={styles.summaryCount}>{errorRows.length}</span> rows have errors
              </div>
            )}

            <div className={styles.actions}>
              <button className={styles.btnCancel} onClick={() => setStep('pick')}>Back</button>
              {errorRows.length > 0 && validRows.length > 0 && (
                <button
                  className={styles.btnSkipErrors}
                  onClick={() => doImport(validRows)}
                >
                  Import {validRows.length} valid rows
                </button>
              )}
              {validRows.length > 0 && errorRows.length === 0 && (
                <button className={styles.btnImport} onClick={() => doImport(validRows)}>
                  Import {validRows.length} rows
                </button>
              )}
            </div>
          </>
        )}

        {/* ── Step: Importing ───────────────────────────────────── */}
        {step === 'importing' && (
          <div className={styles.importing}>Importing records...</div>
        )}

        {/* ── Step: Done ────────────────────────────────────────── */}
        {step === 'done' && (
          <>
            <div className={styles.success}>
              Successfully imported {importedCount} {resourceName.toLowerCase()}.
            </div>
            <div className={styles.actions}>
              <button className={styles.btnImport} onClick={onClose}>Done</button>
            </div>
          </>
        )}

        {/* ── Step: Error ───────────────────────────────────────── */}
        {step === 'error' && (
          <>
            <div className={styles.errorMsg}>{errorMsg}</div>
            <div className={styles.actions}>
              <button className={styles.btnCancel} onClick={() => { setStep('pick'); setErrorMsg(''); }}>
                Try Again
              </button>
              <button className={styles.btnCancel} onClick={onClose}>Close</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
